export type HealthPackage = {
  id: string;
  name: string;
  category: 'Individual' | 'Corporate' | 'Executive' | 'Family' | 'Senior Citizen';
  price: number;
  originalPrice: number;
  discount: number;
  tests: number;
  popular: boolean;
  includes: string[];
  sampleType: string;
  fasting: boolean;
  turnaround: string;
  highlight?: boolean;
  badge?: string;
  description: string;
};

// Health packages — package-level pricing (not individual test rates)
// Individual test rates are in src/app/data/testRates.ts
export const allPackages: HealthPackage[] = [
  {
    id: 'pkg-basic-health',
    name: 'Basic Health Check',
    category: 'Individual',
    price: 799,
    originalPrice: 1200,
    discount: 33,
    tests: 35,
    popular: false,
    highlight: false,
    includes: [
      'CBC-Complete Blood Count (₹190)',
      'ESR (₹200)',
      'Sugar (Glucose) Fasting (₹70)',
      'Urine Complete (₹300)',
      'Lipid Profile (₹715)',
      'LFT (Liver Function Test) (₹1,045)',
      'Kidney Profile (₹320)',
    ],
    sampleType: 'Blood + Urine',
    fasting: true,
    turnaround: '24 hrs',
    description: 'Essential health screening for working adults. Covers all basic blood parameters.',
  },
  {
    id: 'pkg-complete-health',
    name: 'Complete Health Profile',
    category: 'Individual',
    price: 1499,
    originalPrice: 2800,
    discount: 46,
    tests: 72,
    popular: true,
    highlight: true,
    badge: 'Most Popular',
    includes: [
      'CBC-Complete Blood Count (₹190)',
      'ESR (₹200)',
      'Thyroid Profile - T3, T4, TSH (₹495)',
      'Lipid Profile (₹715)',
      'Kidney Profile (₹320)',
      'LFT (Liver Function Test) (₹1,045)',
      '25 OH Vitamin D (₹1,280)',
      'Vitamin B12 (₹990)',
      'Hba1c (Whole Blood) (₹495)',
    ],
    sampleType: 'Blood + Urine',
    fasting: true,
    turnaround: '24 hrs',
    description: 'Our bestselling comprehensive checkup. 72 parameters covering all major organ systems.',
  },
  {
    id: 'pkg-diabetes-care',
    name: 'Diabetes Care Package',
    category: 'Individual',
    price: 999,
    originalPrice: 1800,
    discount: 44,
    tests: 28,
    popular: false,
    includes: [
      'Hba1c (Whole Blood) (₹495)',
      'Sugar (Glucose) Fasting (₹70)',
      'Sugar (Glucose) Post Prandial (₹70)',
      'Insulin Fasting (₹770)',
      'Microalbumin (₹575)',
      'Lipid Profile (₹715)',
      'Kidney Profile (₹320)',
    ],
    sampleType: 'Blood + Urine',
    fasting: true,
    turnaround: '24 hrs',
    description: 'Designed for diabetics and pre-diabetics. Tracks glucose control and organ health.',
  },
  {
    id: 'pkg-cardiac-risk',
    name: 'Cardiac Risk Assessment',
    category: 'Individual',
    price: 1299,
    originalPrice: 2400,
    discount: 46,
    tests: 42,
    popular: false,
    includes: [
      'Lipid Profile (₹715)',
      'Homocysteine (₹1,155)',
      'High Sensitivity C-Reactive Protein (hs-CRP) (₹750)',
      'Troponine T (₹1,800)',
      'GD Wellness Cardiac Risk Marker (₹1,210)',
      'NT-Pro BNP (₹3,450)',
    ],
    sampleType: 'Blood + ECG',
    fasting: true,
    turnaround: '24 hrs',
    description: 'Comprehensive heart risk profiling. Recommended for anyone above 35 years.',
  },
  {
    id: 'pkg-womens-health',
    name: "Women's Health Package",
    category: 'Individual',
    price: 1799,
    originalPrice: 3200,
    discount: 44,
    tests: 58,
    popular: false,
    badge: 'For Women',
    includes: [
      'CBC-Complete Blood Count (₹190)',
      'Thyroid Profile - T3, T4, TSH (₹495)',
      'FSH, LH & Prolactin (₹1,500)',
      'Estradiol (₹605)',
      'PAP Smear - Conventional (₹825)',
      '25 OH Vitamin D (₹1,280)',
      'Vitamin B12 (₹990)',
      'Iron Studies (₹660)',
      'Urine Complete (₹300)',
    ],
    sampleType: 'Blood + Urine + Imaging',
    fasting: true,
    turnaround: '48 hrs',
    description: "Complete health screening tailored for women's unique health needs.",
  },
  {
    id: 'pkg-senior-citizen',
    name: 'Senior Citizen Care',
    category: 'Senior Citizen',
    price: 2199,
    originalPrice: 4500,
    discount: 51,
    tests: 98,
    popular: false,
    badge: '51% OFF',
    includes: [
      'CBC-Complete Blood Count (₹190)',
      'GD Wellness Cardiac Risk Marker (₹1,210)',
      'Calcium (₹176)',
      '25 OH Vitamin D (₹1,280)',
      'Prostate Specific Antigen (PSA)-Total (₹770)',
      'Kidney Profile (₹320)',
      'LFT (Liver Function Test) (₹1,045)',
      'Thyroid Profile - T3, T4, TSH (₹495)',
      'Hba1c (Whole Blood) (₹495)',
    ],
    sampleType: 'Blood + Urine + ECG + X-Ray',
    fasting: true,
    turnaround: '48 hrs',
    description: 'Comprehensive age-appropriate health checkup for seniors aged 60 and above.',
  },
  {
    id: 'pkg-senior-plus',
    name: 'Senior Citizen Premium',
    category: 'Senior Citizen',
    price: 3299,
    originalPrice: 7000,
    discount: 53,
    tests: 142,
    popular: false,
    includes: [
      'All Senior Citizen Care tests',
      '2D Echocardiography',
      'Ultrasound Abdomen & Pelvis',
      'Ophthalmology Consultation',
      'Dietician Consultation',
      'Bone Density Scan',
    ],
    sampleType: 'Blood + Urine + ECG + Imaging',
    fasting: true,
    turnaround: '48 hrs',
    description: 'The gold standard senior health package with imaging and specialist consultations.',
  },
  {
    id: 'pkg-executive',
    name: 'Executive Health Checkup',
    category: 'Executive',
    price: 3499,
    originalPrice: 7000,
    discount: 50,
    tests: 124,
    popular: false,
    badge: 'Executive',
    includes: [
      'Full Body Blood Profile',
      '2D Echo + ECG',
      'Ultrasound Abdomen',
      'Carcino Embryonic Antigen (CEA) (₹715)',
      'Prostate Specific Antigen (PSA)-Total (₹770)',
      'Pulmonary Function Test (PFT)',
      'Physician Consultation',
    ],
    sampleType: 'Blood + Urine + ECG + Imaging',
    fasting: true,
    turnaround: '48 hrs',
    description: 'Premium executive health package for senior professionals. Includes imaging and specialist consult.',
  },
  {
    id: 'pkg-executive-plus',
    name: 'Executive Premium Checkup',
    category: 'Executive',
    price: 5499,
    originalPrice: 12000,
    discount: 54,
    tests: 168,
    popular: false,
    includes: [
      'All Executive tests',
      'MRI Brain Screening',
      'Full Body PET/CT Scan',
      'Cardiologist Consultation',
      'Gastroenterologist Consultation',
      'Annual Health Tracker Report',
    ],
    sampleType: 'Blood + Urine + Imaging + Consultations',
    fasting: true,
    turnaround: '72 hrs',
    description: 'The most comprehensive executive package with advanced imaging and specialist consultations.',
  },
  {
    id: 'pkg-family-basic',
    name: 'Family Health Package',
    category: 'Family',
    price: 2799,
    originalPrice: 5000,
    discount: 44,
    tests: 35,
    popular: false,
    badge: 'Family of 3',
    includes: [
      'CBC-Complete Blood Count ×3 (₹190 each)',
      'Sugar (Glucose) Fasting ×3 (₹70 each)',
      'Urine Complete ×3 (₹300 each)',
      'Lipid Profile ×3 (₹715 each)',
      'LFT (Liver Function Test) ×3 (₹1,045 each)',
    ],
    sampleType: 'Blood + Urine',
    fasting: true,
    turnaround: '24 hrs',
    description: 'Basic health screening for a family of 3. Same tests for all members.',
  },
  {
    id: 'pkg-family-complete',
    name: 'Family Complete Package',
    category: 'Family',
    price: 4999,
    originalPrice: 9000,
    discount: 44,
    tests: 72,
    popular: false,
    badge: 'Family of 4',
    includes: [
      'Complete Health Profile ×4',
      'Thyroid Profile - T3, T4, TSH ×4 (₹495 each)',
      '25 OH Vitamin D ×4 (₹1,280 each)',
      'Vitamin B12 ×4 (₹990 each)',
      'Hba1c (Whole Blood) ×4 (₹495 each)',
      'Urine Complete ×4 (₹300 each)',
    ],
    sampleType: 'Blood + Urine',
    fasting: true,
    turnaround: '24 hrs',
    description: 'Comprehensive health checkup for a family of 4. All major parameters covered.',
  },
  {
    id: 'pkg-corporate-basic',
    name: 'Corporate Basic Checkup',
    category: 'Corporate',
    price: 499,
    originalPrice: 900,
    discount: 44,
    tests: 22,
    popular: false,
    badge: 'Per Employee',
    includes: [
      'CBC-Complete Blood Count (₹190)',
      'ESR (₹200)',
      'Sugar (Glucose) Fasting (₹70)',
      'Urine Complete (₹300)',
      'Blood Pressure',
      'BMI',
    ],
    sampleType: 'Blood + Urine',
    fasting: true,
    turnaround: '24 hrs',
    description: 'Cost-effective annual health checkup for large employee groups. Min 25 employees.',
  },
  {
    id: 'pkg-corporate-standard',
    name: 'Corporate Standard Checkup',
    category: 'Corporate',
    price: 899,
    originalPrice: 1600,
    discount: 44,
    tests: 42,
    popular: false,
    badge: 'Per Employee',
    includes: [
      'CBC-Complete Blood Count (₹190)',
      'Lipid Profile (₹715)',
      'Sugar (Glucose) Fasting (₹70)',
      'TSH (Thyroid Stimulating Hormone) (₹275)',
      'Kidney Profile (₹320)',
      'LFT (Liver Function Test) (₹1,045)',
      'ECG',
      'Chest X-Ray',
    ],
    sampleType: 'Blood + Urine + ECG + X-Ray',
    fasting: true,
    turnaround: '48 hrs',
    description: 'Comprehensive corporate annual checkup. Covers all major organ systems. Min 10 employees.',
  },
  {
    id: 'pkg-pre-employment',
    name: 'Pre-Employment Medical',
    category: 'Corporate',
    price: 699,
    originalPrice: 1200,
    discount: 42,
    tests: 30,
    popular: false,
    includes: [
      'CBC-Complete Blood Count (₹190)',
      'ABO Blood Group and Rh Type (₹220)',
      'Urine Complete (₹300)',
      'Sugar (Glucose) Fasting (₹70)',
      'Chest X-Ray',
      'Eye Test',
      'Audiometry',
      'Fitness Certificate',
    ],
    sampleType: 'Blood + Urine + Imaging',
    fasting: false,
    turnaround: '24 hrs',
    description: 'Legally compliant pre-employment fitness assessment with certificate. NABL-certified reports.',
  },
  {
    id: 'pkg-insurance-medical',
    name: 'Insurance Medical Panel',
    category: 'Corporate',
    price: 799,
    originalPrice: 1400,
    discount: 43,
    tests: 28,
    popular: false,
    badge: 'Insurance Approved',
    includes: [
      'CBC-Complete Blood Count (₹190)',
      'Urine Complete (₹300)',
      'Sugar (Glucose) Fasting (₹70)',
      'Lipid Profile (₹715)',
      'LFT (Liver Function Test) (₹1,045)',
      'Kidney Profile (₹320)',
      'ECG',
      'Chest X-Ray',
      'Medical Officer Certificate',
    ],
    sampleType: 'Blood + Urine + ECG + X-Ray',
    fasting: true,
    turnaround: '24 hrs',
    description: 'Insurance company approved panel. Reports accepted by all major insurers.',
  },
];